import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { TrendingUp, TrendingDown, Minus } from "lucide-react"

const statCardVariants = cva(
  "rounded-xl border bg-card p-6",
  {
    variants: {
      size: {
        default: "p-6",
        sm: "p-4",
        lg: "p-8",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

export interface StatCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statCardVariants> {
  title: string
  value: string | number
  subtitle?: string
  trend?: {
    value: number
    label: string
    direction?: "up" | "down" | "neutral"
  }
  icon?: React.ReactNode
  loading?: boolean
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, size, title, value, subtitle, trend, icon, loading, ...props }, ref) => {
    const getTrendColor = (direction?: "up" | "down" | "neutral") => {
      switch (direction) {
        case "up":
          return "text-success"
        case "down":
          return "text-destructive"
        default:
          return "text-muted-foreground"
      }
    }

    const TrendIcon = trend?.direction === "up"
      ? TrendingUp
      : trend?.direction === "down"
        ? TrendingDown
        : Minus

    if (loading) {
      return (
        <div ref={ref} className={cn(statCardVariants({ size }), className)} {...props}>
          <div className="animate-pulse space-y-3">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-8 w-16 bg-muted rounded" />
            <div className="h-3 w-20 bg-muted rounded" />
          </div>
        </div>
      )
    }

    return (
      <div ref={ref} className={cn(statCardVariants({ size }), className)} {...props}>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-semibold tracking-tight tabular-nums">{value}</p>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
            {trend && (
              <div className={cn("flex items-center gap-1 text-sm", getTrendColor(trend.direction))}>
                <TrendIcon className="h-4 w-4" />
                <span className="font-medium">{trend.value > 0 ? "+" : ""}{trend.value}</span>
                <span className="text-muted-foreground">{trend.label}</span>
              </div>
            )}
          </div>
          {icon && (
            <div className="rounded-lg bg-accent p-2 text-accent-foreground">
              {icon}
            </div>
          )}
        </div>
      </div>
    )
  }
)
StatCard.displayName = "StatCard"

export { StatCard, statCardVariants }
